import { type Tag, type Trail, type TrailCapture } from '@prisma/client'
import React from 'react'
import { Link, useLocation } from 'react-router'
import { SkeletonImage } from '#app/components/skeleton-image.tsx'
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '#app/components/ui/hover-card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { useSmartThumbnailCache } from '#app/utils/smart-cache.tsx'
import { toExplorePath } from '../routes/explore_+/_to-path.ts'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from './ui/card'

export interface TrailCardProps {
	trail: Omit<Trail, 'defaultCaptureId' | 'createdAt' | 'updatedAt'>
	trailCapture: Omit<
		TrailCapture,
		'trailId' | 'ownerId' | 'createdAt' | 'updatedAt'
	> & {
		owner?: { username: string }
		tags: { tag: Omit<Tag, 'id' | 'createdAt' | 'updatedAt'> }[]
	}
	showCaptureDescription?: boolean
	role?: string
	tagLinks?: boolean
}

const TrailCard: React.FC<TrailCardProps> = ({
	trail,
	trailCapture,
	showCaptureDescription = true,
	role,
	tagLinks = false,
}) => {
	const location = useLocation()
	const existingSearchParams = new URLSearchParams(location.search)

	const { getSmartThumbnailUrl } = useSmartThumbnailCache()
	const thumbnailUrl = getSmartThumbnailUrl(
		trailCapture.id,
		trailCapture.defaultFrameId || '0',
	)

	const defaultFramePath = toExplorePath(
		trail.country,
		trail.region,
		'trail',
		trail.slug,
		trailCapture?.slug,
		trailCapture?.defaultFrameId || '0',
	)

	const descriptionText =
		trailCapture && showCaptureDescription
			? trailCapture.description
			: trail.details

	const ownerAttribution = trailCapture.owner && (
		<small className="mt-4 block text-right text-xs text-muted-foreground">
			Contributed by{' '}
			<Link to={`/users/${trailCapture.owner.username}`}>
				{trailCapture.owner.username}
			</Link>
		</small>
	)

	return (
		<Card
			role={role}
			className="mb-4 flex h-full flex-col overflow-hidden sm:mb-6 md:mb-8"
		>
			<Link to={defaultFramePath}>
				<SkeletonImage src={thumbnailUrl} alt={trail.name} />
			</Link>

			<CardHeader className="px-2 sm:px-4 md:px-6">
				<CardTitle>
					<div className="flex items-center space-x-1">
						<Link to={defaultFramePath}>{trail.name}</Link>
						{showCaptureDescription && (
							<HoverCard openDelay={0} closeDelay={200}>
								<HoverCardTrigger asChild>
									<button
										type="button"
										aria-label="Trail info"
										className="inline-flex items-center p-1"
									>
										<Icon
											name="question-mark-circled"
											className="h-6 w-6 text-muted"
										/>
									</button>
								</HoverCardTrigger>
								<HoverCardContent
									className="border-2 p-4 text-base font-normal"
									side="bottom"
									align="start"
								>
									{trail.details}
								</HoverCardContent>
							</HoverCard>
						)}
					</div>
					<CardDescription className="mt-1 text-sm">
						{trail.country.toUpperCase()}{' '}
						{trail.region.charAt(0).toUpperCase() + trail.region.slice(1)}
					</CardDescription>
				</CardTitle>
			</CardHeader>

			<CardContent className="flex flex-1 flex-col justify-between px-2 sm:px-4 md:px-6">
				<div>
					<p className="text-base">{descriptionText}</p>
					{tagLinks && (
						<div className="mt-3 overflow-auto sm:mt-4 md:mt-6">
							{trailCapture.tags.map(({ tag }) => {
								const newSearch = new URLSearchParams(existingSearchParams)
								if (
									!newSearch.has('tags') ||
									!newSearch.getAll('tags').includes(tag.slug)
								) {
									newSearch.append('tags', tag.slug)
								}
								const newUrl = `${location.pathname}?${newSearch.toString()}`
								return (
									<span
										key={tag.slug}
										className="mr-1 mb-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold sm:mr-2 sm:mb-2 sm:px-3 sm:py-1 sm:text-sm"
									>
										<Link to={newUrl} rel="nofollow">
											{tag.name}
										</Link>
									</span>
								)
							})}
						</div>
					)}
				</div>
				{ownerAttribution}
			</CardContent>
		</Card>
	)
}

export default TrailCard
